import React, { Fragment, useEffect, useState } from "react";
import {
  createStyles,
  makeStyles,
  Theme,
  Paper,
  Grid,
  Typography,
  Button,
} from "@material-ui/core";
import { Dropdown, DropdownOption } from "components/Dropdown";
import { DatePickerButton } from "components/DatePickerButton";
import { dailyRosterService } from "@dwo/shared/dist/services/dailyRosterService";
import { getAllRegions, selectRegions } from "features/region/regionSlice";
import { useSelector, useDispatch } from "react-redux";
import { parseRegionsToDropdownOptions } from "utils/summaryUtils";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      fontSize: "20px",
      color: theme.palette.primary.main,
      fontWeight: 400,
      marginBottom: "16px",
    },
    paper: {
      backgroundColor: "white",
      margin: "0 24px 24px 24px",
      padding: "24px",
      borderRadius: 0,
      position: "relative",
    },
    historyFiltersContainer: {
      justifyContent: "flex-end",

      [theme.breakpoints.down("xs")]: {
        justifyContent: "flex-start",
      },
    },
    caption: {
      fontSize: "14px",
      color: theme.palette.grey[500],
      fontWeight: 400,
      marginBottom: "16px",
    },
    paginationContainer: {
      borderRadius: "2px",
      margin: "0 24px 24px",
    },
    rowSelectLabel: {
      color: theme.palette.primary.dark,
      fontWeight: "bold",
    },
    customButton: {
      width: "100%",
    },
    statusText: {
      fontSize: "14px",
      fontWeight: 600,
      justifyContent: "center",
    },
  }),
);

export function DownloadDailyRoster() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const fetchedRegions = useSelector(selectRegions);
  const regionsOptions: DropdownOption[] =
    parseRegionsToDropdownOptions(fetchedRegions) || [];
  const [customRegion, setCustomRegion] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  useEffect(() => {
    dispatch(getAllRegions());
  }, []);

  const handleSelectCustomRegion = (selectedRegion: string) => {
    setCustomRegion(selectedRegion);
  };

  const handleDateChange = (date: Date) => {
    setSelectedDate(new Date(date));
  };

  const handleClickDownloadCustomFile = async () => {
    const region = customRegion === "all" ? "" : customRegion;
    const date = selectedDate.toISOString().split("T")[0];
    const fileName = region
      ? `daily-roster-${date}-${
          regionsOptions.find((option) => option.value === region)?.label
        }.csv`
      : `daily-roster-${date}.csv`;

    await dailyRosterService.downloadDailyRosterCSVFile(fileName, {
      where: { region, date },
    });
  };

  return (
    <Fragment>
      <Paper className={classes.paper}>
        <Grid container direction="column">
          <Grid item sm={6} xs={12}>
            <Typography className={classes.header}>
              Daily Roster History
            </Typography>
          </Grid>
          <Grid item sm={6} xs={12}>
            <Typography className={classes.caption}>
              Select region and date range, you will get a file with all the
              information in "csv" format.
              <br />
              This file can only be retrieved on ranges of one day
            </Typography>
          </Grid>

          <Grid item sm={7} xs={12}>
            <Grid container spacing={2}>
              <Grid item sm={3} xs={12}>
                <Dropdown
                  id="exportRegionDropdown"
                  label="Region"
                  options={regionsOptions}
                  selectedValue={customRegion}
                  onSelectOption={handleSelectCustomRegion}
                />
              </Grid>
              <Grid item sm={7} xs={12}>
                <DatePickerButton
                  dateValue={selectedDate}
                  disabledFuture
                  onSelectDate={handleDateChange}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Fragment>
                  <Button
                    variant="contained"
                    className={classes.customButton}
                    onClick={handleClickDownloadCustomFile}
                  >
                    Download File
                  </Button>
                </Fragment>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Fragment>
  );
}
